import { Component } from '@theme/component';

/**
 * Client-side infinite scroll for the collections list page.
 * All collection cards are rendered server-side but hidden beyond the initial batch.
 * As the user scrolls, more cards are revealed in batches.
 */
export default class CollectionListPaginated extends Component {
  /** @type {IntersectionObserver | undefined} */
  #scrollObserver;

  /** @type {number} */
  #batchSize = 20;

  /** @type {number} */
  #visibleCount = 0;

  connectedCallback() {
    super.connectedCallback();

    const { grid } = this.refs;
    if (!grid) return;

    this.#batchSize = Number(grid.dataset.batchSize) || 20;

    const items = grid.querySelectorAll(':scope > .resource-list__item');

    // Hide all items beyond the first batch
    items.forEach((item, i) => {
      if (i >= this.#batchSize) {
        item.style.display = 'none';
      }
    });

    this.#visibleCount = Math.min(this.#batchSize, items.length);

    // If there are more items to show, observe the sentinel
    if (this.#visibleCount < items.length) {
      this.#observeSentinel();
    } else {
      this.#hideSentinel();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollObserver?.disconnect();
  }

  #observeSentinel() {
    const { sentinel } = this.refs;
    if (!sentinel) return;

    this.#scrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#showNextBatch();
          }
        }
      },
      { rootMargin: '200px' }
    );

    this.#scrollObserver.observe(sentinel);
  }

  #showNextBatch() {
    const { grid } = this.refs;
    if (!grid) return;

    const items = grid.querySelectorAll(':scope > .resource-list__item');
    const end = Math.min(this.#visibleCount + this.#batchSize, items.length);

    for (let i = this.#visibleCount; i < end; i++) {
      items[i].style.display = '';
    }

    this.#visibleCount = end;

    // If all items are now visible, stop observing
    if (this.#visibleCount >= items.length) {
      this.#scrollObserver?.disconnect();
      this.#hideSentinel();
    }
  }

  #hideSentinel() {
    const { sentinel } = this.refs;
    if (sentinel) sentinel.style.display = 'none';
  }
}

if (!customElements.get('collection-list-paginated')) {
  customElements.define('collection-list-paginated', CollectionListPaginated);
}
